// Utility functions for JumpBean
/**
 * Default time thresholds per difficulty level (in seconds)
 * Higher difficulty = more time allowed before penalty
 */
export const DEFAULT_TIME_THRESHOLDS = {
    1: 30, // Easy - 30 seconds
    2: 45, // Normal - 45 seconds
    3: 60, // Medium - 60 seconds
    4: 90, // Hard - 90 seconds
    5: 120, // Very Hard - 2 minutes
};
/**
 * Calculate stars earned based on attempts only (simple version)
 *
 * Star Calculation Rules:
 * - 3 stars: First try correct (Perfect!)
 * - 2 stars: Second try correct (Great!)
 * - 1 star: Third or more tries (Good try! - Always at least 1 star)
 *
 * @param attempts - Number of attempts made (1-based)
 * @returns Stars earned (1, 2, or 3)
 */
export function calculateStars(attempts) {
    if (attempts === 1)
        return 3; // Perfect!
    if (attempts === 2)
        return 2; // Great!
    return 1; // Good try! (always earn at least 1 star)
}
/**
 * Calculate stars earned based on attempts, time spent, and difficulty
 *
 * Star Calculation Rules (per issue #31):
 * - 3 stars: First try correct AND within time threshold
 * - 2 stars: 2 attempts OR over time threshold (but first try)
 * - 1 star: 3+ attempts (always earn at least 1 star)
 *
 * The time threshold scales with difficulty - harder challenges
 * give more time before a penalty is applied.
 *
 * @param attempts - Number of attempts made (1-based)
 * @param timeSpentSeconds - Time spent on the challenge in seconds
 * @param challengeDifficulty - Difficulty level (1-5), defaults to 1
 * @returns Stars earned (1, 2, or 3)
 */
export function calculateStarsWithTime(attempts, timeSpentSeconds, challengeDifficulty = 1) {
    // Clamp difficulty to valid range
    const difficulty = Math.max(1, Math.min(5, Math.round(challengeDifficulty)));
    // Get time threshold for this difficulty
    const timeThreshold = DEFAULT_TIME_THRESHOLDS[difficulty] ?? 30;
    // Check if over time threshold
    const overTimeThreshold = timeSpentSeconds > timeThreshold;
    // 3+ attempts always = 1 star
    if (attempts >= 3)
        return 1;
    // 2 attempts = 2 stars
    if (attempts === 2)
        return 2;
    // First try correct
    if (attempts === 1) {
        // If over time threshold, reduce to 2 stars
        if (overTimeThreshold)
            return 2;
        // Perfect - first try within time
        return 3;
    }
    // Fallback (shouldn't reach here)
    return 1;
}
/**
 * Format time in seconds to a friendly display string
 */
export function formatPlayTime(seconds) {
    if (seconds < 60)
        return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    if (minutes < 60)
        return `${minutes}m ${remainingSeconds}s`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes}m`;
}
//# sourceMappingURL=index.js.map